import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Home, Stethoscope, HeartPulse, FlaskConical, AlertTriangle, MapPin, CheckCircle2, Star } from 'lucide-react';
import { professionals } from '../../data/mockData';
export function HomeVisitFlow() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [service, setService] = useState('');
  const [condition, setCondition] = useState('');
  const [matchedProvider, setMatchedProvider] = useState<any>(null);
  const [trackingState, setTrackingState] = useState(0);
  const services = [{
    id: 'Doctor',
    icon: Stethoscope,
    desc: 'General consultation at home'
  }, {
    id: 'Nurse',
    icon: HeartPulse,
    desc: 'Nursing care & wound dressing'
  }, {
    id: 'Lab',
    icon: FlaskConical,
    desc: 'Sample collection at home'
  }];
  const handleMatch = () => {
    setStep(3);
    setTimeout(() => {
      setMatchedProvider(professionals[1]); // Mock match
      setStep(4);
    }, 2000);
  };
  const handlePayment = () => {
    setStep(6);
    const t1 = setTimeout(() => setTrackingState(1), 2000);
    const t2 = setTimeout(() => setTrackingState(2), 4000);
    const t3 = setTimeout(() => setTrackingState(3), 6000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  };
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-607">
      <div className="bg-white px-6 pt-12 pb-4 shadow-sm sticky top-0 z-10" data-id="element-608">
        <div className="flex items-center gap-3 mb-4" data-id="element-609">
          <button onClick={() => step > 1 && step < 6 ? setStep(step - 1) : navigate(-1)} className="p-2 -ml-2 hover:bg-slate-50 rounded-full" data-id="element-610">
            <ArrowLeft className="w-6 h-6 text-slate-700" data-id="element-611" />
          </button>
          <h1 className="text-xl font-bold text-slate-900" data-id="element-612">
            Request Home Visit
          </h1>
        </div>
        {step < 6 && <div className="flex gap-2" data-id="element-613">
            {[1, 2, 3, 4, 5].map(i => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-purple-600' : 'bg-slate-200'}`} data-id="element-614" />)}
          </div>}
      </div>

      <div className="p-6 flex-1 overflow-y-auto" data-id="element-615">
        {step === 1 && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-4" data-id="element-616">
            <h2 className="font-semibold text-slate-900 mb-2" data-id="element-617">
              Who do you need?
            </h2>
            <div className="space-y-3" data-id="element-618">
              {services.map(srv => <button key={srv.id} onClick={() => {
            setService(srv.id);
            setStep(2);
          }} className="w-full bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4 active:scale-95 transition-transform hover:border-purple-200" data-id="element-619">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center shrink-0" data-id="element-620">
                    <srv.icon className="w-6 h-6 text-purple-600" data-id="element-621" />
                  </div>
                  <div className="text-left" data-id="element-622">
                    <h3 className="font-bold text-slate-900" data-id="element-623">
                      {srv.id} Home Visit
                    </h3>
                    <p className="text-sm text-slate-500" data-id="element-624">{srv.desc}</p>
                  </div>
                </button>)}
            </div>
          </motion.div>}

        {step === 2 && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-4" data-id="element-625">
            <h2 className="font-semibold text-slate-900 mb-2" data-id="element-626">
              What is the condition?
            </h2>
            <div className="space-y-3" data-id="element-627">
              {['General Illness', 'Follow-up Visit'].map(cond => <button key={cond} onClick={() => {
            setCondition(cond);
            handleMatch();
          }} className="w-full bg-white p-4 rounded-xl shadow-sm border border-slate-100 text-left font-medium text-slate-700 hover:border-purple-200" data-id="element-628">
                  {cond}
                </button>)}
              <button onClick={() => navigate('/emergency')} className="w-full bg-red-50 p-4 rounded-xl border border-red-200 text-left flex items-center justify-between" data-id="element-629">
                <div data-id="element-630">
                  <span className="font-bold text-red-700 block" data-id="element-631">
                    Emergency
                  </span>
                  <span className="text-xs text-red-600" data-id="element-632">
                    Life-threatening conditions
                  </span>
                </div>
                <AlertTriangle className="text-red-600" data-id="element-633" />
              </button>
            </div>
          </motion.div>}

        {step === 3 && <div className="flex flex-col items-center justify-center h-full text-center py-20" data-id="element-634">
            <div className="relative w-24 h-24 mb-6" data-id="element-635">
              <div className="absolute inset-0 bg-purple-200 rounded-full animate-ping opacity-75" data-id="element-636"></div>
              <div className="relative w-full h-full bg-purple-100 rounded-full flex items-center justify-center" data-id="element-637">
                <Home className="w-10 h-10 text-purple-600" data-id="element-638" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-639">
              Finding the best provider...
            </h2>
            <p className="text-slate-500" data-id="element-640">
              Matching you with a top-rated {service.toLowerCase()} nearby.
            </p>
          </div>}

        {step === 4 && matchedProvider && <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="space-y-6" data-id="element-641">
            <h2 className="text-xl font-bold text-slate-900 text-center" data-id="element-642">
              Provider Found!
            </h2>
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 text-center" data-id="element-643">
              <img src={matchedProvider.image} className="w-24 h-24 rounded-full object-cover mx-auto mb-3 border-4 border-purple-50" data-id="element-644" />
              <h3 className="font-bold text-lg text-slate-900" data-id="element-645">
                {matchedProvider.name}
              </h3>
              <p className="text-sm text-slate-500 mb-3" data-id="element-646">
                {matchedProvider.specialty}
              </p>
              <div className="flex justify-center gap-4 text-sm font-medium" data-id="element-647">
                <span className="flex items-center" data-id="element-648">
                  <Star className="w-4 h-4 text-amber-400 mr-1 fill-current" data-id="element-649" />{' '}
                  {matchedProvider.rating}
                </span>
                <span className="flex items-center text-purple-600 bg-purple-50 px-2 py-1 rounded-md" data-id="element-650">
                  ETA: 20 mins
                </span>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm" data-id="element-651">
              <div className="flex items-start gap-3" data-id="element-652">
                <MapPin className="w-5 h-5 text-slate-400 mt-0.5" data-id="element-653" />
                <div data-id="element-654">
                  <p className="font-medium text-slate-900" data-id="element-655">14 Admiralty Way</p>
                  <p className="text-sm text-slate-500" data-id="element-656">Lekki Phase 1, Lagos</p>
                </div>
              </div>
            </div>

            <button onClick={() => setStep(5)} className="w-full bg-purple-600 text-white py-3.5 rounded-xl font-semibold text-lg shadow-sm hover:bg-purple-700" data-id="element-657">
              Confirm Booking
            </button>
          </motion.div>}

        {step === 5 && matchedProvider && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-6" data-id="element-658">
            <h2 className="text-xl font-bold text-slate-900" data-id="element-659">
              Payment Summary
            </h2>
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3" data-id="element-660">
              <div className="flex justify-between text-sm" data-id="element-661">
                <span className="text-slate-500" data-id="element-662">Home Visit Fee</span>
                <span className="font-medium" data-id="element-663">
                  ₦{matchedProvider.fee.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-664">
                <span className="text-slate-500" data-id="element-665">Platform Fee</span>
                <span className="font-medium" data-id="element-666">₦1,000</span>
              </div>
              <div className="pt-3 border-t border-slate-100 flex justify-between font-bold text-lg" data-id="element-667">
                <span data-id="element-668">Total</span>
                <span className="text-purple-600" data-id="element-669">
                  ₦{(matchedProvider.fee + 1000).toLocaleString()}
                </span>
              </div>
            </div>
            <button onClick={handlePayment} className="w-full bg-purple-600 text-white py-3.5 rounded-xl font-semibold text-lg shadow-sm hover:bg-purple-700" data-id="element-670">
              Pay ₦{(matchedProvider.fee + 1000).toLocaleString()}
            </button>
          </motion.div>}

        {step === 6 && matchedProvider && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-671">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5" data-id="element-672">
              <h3 className="font-bold text-slate-900 mb-6 text-center" data-id="element-673">
                Live Tracking
              </h3>
              <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px before:h-full before:w-0.5 before:bg-slate-200" data-id="element-674">
                {['Provider Assigned', 'On the Way', 'Arrived', 'Service Complete'].map((status, idx) => <div key={status} className="relative flex items-center gap-4" data-id="element-675">
                    <div className={`w-10 h-10 rounded-full border-4 border-white flex items-center justify-center z-10 transition-colors duration-500 ${trackingState >= idx ? 'bg-purple-600 text-white' : 'bg-slate-200 text-slate-400'}`} data-id="element-676">
                      <CheckCircle2 className="w-5 h-5" data-id="element-677" />
                    </div>
                    <span className={`font-medium ${trackingState >= idx ? 'text-slate-900' : 'text-slate-400'}`} data-id="element-678">
                      {status}
                    </span>
                  </div>)}
              </div>
            </div>
            {trackingState === 3 && <button onClick={() => navigate('/rating')} className="w-full bg-purple-600 text-white py-3.5 rounded-xl font-semibold text-lg shadow-sm animate-fade-in" data-id="element-679">
                Rate Provider
              </button>}
          </motion.div>}
      </div>
    </div>;
}